import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { ICellRendererParams, IRowNode } from '@ag-grid-community/core';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { IconComponent } from '@shared/components/icon/icon.component';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { OrganizationQuery } from '@shared/store/organization/organization.query';

type BudgetLibraryActionsParams = ICellRendererParams & {
  baselineClickFN?: (params: { rowNode: IRowNode }) => void;
  deleteClickFN?: (params: { rowNode: IRowNode }) => void;
  downloadClickFN?: (params: { rowNode: IRowNode }) => void;
  hideDeleteButton?: boolean;
  hideDownloadButton?: boolean;
  hideIsCurrent?: boolean;
  hideRadioButton?: boolean;
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TooltipDirective, NgClass, IconComponent],
  styles: [
    `
      :host {
        display: flex;
        align-items: center;
        height: 100%;
      }
    `,
  ],
  template: `
    @if (params?.data) {
      <div class="flex items-center justify-center space-x-2">
        <div
          class="flex items-center"
          [auxTooltip]="
            params?.data?.bucket_key === ''
              ? 'No download available. Budget created in-application.'
              : ''
          "
        >
          @if (!params?.hideDownloadButton) {
            <button
              class="h-8 w-8 p-0 text-aux-blue"
              type="button"
              auxTooltip="Download"
              [ngClass]="{
                'pointer-events-none opacity-50': !isBucketKeyExist,
              }"
              [disabled]="!isBucketKeyExist"
              (click)="onDownloadClick()"
            >
              <aux-icon name="Download" />
            </button>
          }
        </div>

        @if (
          !params?.hideDeleteButton &&
          ((!params?.data?.is_current && !params?.data?.is_baseline) ||
            params?.data?.budget_type === 'BUDGET_SECONDARY')
        ) {
          <button class="h-8 w-8 p-0" type="button" auxTooltip="Delete" (click)="onDeleteClick()">
            <aux-icon name="Trash" class="text-aux-error" />
          </button>
        }
        @if (
          !params?.hideRadioButton &&
          !params?.hideIsCurrent &&
          params?.data?.budget_type !== 'BUDGET_SECONDARY'
        ) {
          <input
            class="cursor-pointer"
            type="radio"
            [checked]="params?.data?.is_current"
            [disabled]="true"
          />
        }
        @if (
          !params?.hideRadioButton &&
          params?.hideIsCurrent &&
          params?.data?.budget_type !== 'BUDGET_SECONDARY'
        ) {
          <input
            class="cursor-pointer"
            type="radio"
            [ngClass]="{ 'focus:outline-none': params?.hideIsCurrent }"
            [checked]="params?.data?.is_baseline"
            (click)="onBaselineClick()"
          />
        }
      </div>
    }
    @if (params?.value && params?.hideRadioButton) {
      <div class="text-sm font-bold">
        {{ vendorName(params?.value) }}
      </div>
    }
  `,
})
export class BudgetLibraryActionsComponent implements ICellRendererAngularComp {
  organizationQuery = inject(OrganizationQuery);

  isBucketKeyExist = false;
  params?: BudgetLibraryActionsParams;

  agInit(params: BudgetLibraryActionsParams): void {
    this.params = params;

    this.isBucketKeyExist = !!params?.data?.bucket_key;
  }

  onBaselineClick() {
    if (this.params?.baselineClickFN) {
      this.params.baselineClickFN({ rowNode: this.params.node });
    }
  }

  onDeleteClick() {
    if (this.params?.deleteClickFN) {
      this.params.deleteClickFN({ rowNode: this.params.node });
    }
  }

  onDownloadClick() {
    if (this.params?.downloadClickFN) {
      this.params.downloadClickFN({ rowNode: this.params.node });
    }
  }

  refresh(): boolean {
    return false;
  }

  vendorName(id: string) {
    return this.organizationQuery.getEntity(id)?.name || '';
  }
}
